import { FC } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { Box, Typography, styled } from "@mui/material";
import { IconLogo } from "@/assets/icons";
import { RuntimeEnvTag } from "@/components/elements/RuntimeEnvTag";
import { background, divider, text } from "@/theme/colors";
import { elevation, spacing } from "@/theme/variables";
import zIndex from "@mui/material/styles/zIndex";
import { useSharingDetail } from "@/api/services/main/sharing";
import { AppRoutes } from "@/routes/config";

export const SharedLayout: FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const { data: sharingDetail, isLoading, isError } = useSharingDetail(hash);
  const matchName = sharingDetail?.data?.matchName;

  // Redirect to 404 if share URL is invalid or expired — flag as external access to hide home button
  if (isError) {
    return <Navigate to={AppRoutes.pageNotFound} replace state={{ isExternalAccess: true }} />;
  }

  // Show loading while validating share URL to avoid flashing password page for invalid URLs
  if (isLoading) {
    return (
      <Box minHeight="100vh" bgcolor={background.primary}>
        <HeaderWrapper>
          <LogoWrapper>
            <IconLogo />
            <RuntimeEnvTag />
          </LogoWrapper>
        </HeaderWrapper>
      </Box>
    );
  }

  return (
    <Box minHeight="100vh" bgcolor={background.primary}>
      <HeaderWrapper>
        <LogoWrapper>
          <IconLogo />
          <RuntimeEnvTag />
        </LogoWrapper>
        {matchName && (
          <Typography color={text.white} sx={{ fontSize: "14px", lineHeight: "21px", fontWeight: 400 }}>
            {matchName}
          </Typography>
        )}
      </HeaderWrapper>
      <Box>
        <Outlet />
      </Box>
    </Box>
  );
};

const LogoWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

const HeaderWrapper = styled(Box)`
  background-color: ${background.main};
  height: 56px;
  padding: 0px ${spacing.md};
  border-bottom: 1px solid ${divider.middle};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${elevation.lv1};
  position: sticky;
  top: 0;
  z-index: ${zIndex.appBar};
`;
